import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { StrategyBadge } from '@/components/ui/StrategyBadge';
import { PnlDisplay } from '@/components/ui/PnlDisplay';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState, NoDataIcon } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { useChains } from '@/hooks/queries/useChains';
import { getDaysToExpiration } from '@/lib/formatters';
import { cn } from '@/lib/utils';

export function OpenPositionsSummary() {
  const { data: chains, isLoading } = useChains({ status: 'open' });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Open Positions</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  const openChains = chains || [];
  const openCount = openChains.length;

  // Get the nearest expiration from all open positions
  let nearestExpiration: { chain: typeof openChains[0]; dte: number } | null = null;
  for (const chain of openChains) {
    const chainPositions = chain.positions || [];
    for (const position of chainPositions) {
      if (position.status !== 'open') continue;
      const positionLegs = position.legs || [];
      for (const leg of positionLegs) {
        const dte = getDaysToExpiration(leg.expirationDate);
        if (dte >= 0 && (!nearestExpiration || dte < nearestExpiration.dte)) {
          nearestExpiration = { chain, dte };
        }
      }
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Open Positions</CardTitle>
        <Link to="/chains?status=open" className="text-sm text-brand-600 hover:text-brand-700">
          View all
        </Link>
      </CardHeader>
      <CardContent>
        {openCount === 0 ? (
          <EmptyState
            icon={<NoDataIcon />}
            title="No open positions"
            description="Create a new trade to get started"
            action={
              <Link to="/chains/new">
                <Button size="sm">New Trade</Button>
              </Link>
            }
          />
        ) : (
          <div className="space-y-4">
            {/* Summary stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-500">Open Trades</p>
                <p className="text-2xl font-semibold text-gray-900">{openCount}</p>
              </div>
              {nearestExpiration && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-500">Nearest Expiration</p>
                  <p
                    className={cn(
                      'text-2xl font-semibold',
                      nearestExpiration.dte <= 7 ? 'text-yellow-600' : 'text-gray-900'
                    )}
                  >
                    {nearestExpiration.dte}d
                  </p>
                </div>
              )}
            </div>

            {/* Recent open positions */}
            <div className="space-y-2">
              {openChains.slice(0, 5).map((chain) => (
                <Link
                  key={chain.id}
                  to={`/chains/${chain.id}`}
                  className="block p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">{chain.underlyingSymbol}</span>
                      <StrategyBadge strategy={chain.strategyType} size="sm" />
                    </div>
                    <PnlDisplay
                      value={chain.totalPremiumCollected - chain.totalPremiumPaid}
                      size="sm"
                    />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
